// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  chtCoolingRateTriggerFpm,
  chtCoolingRateTriggerMaxFpm,
  chtCoolingRateTriggerMinFpm,
} from "../core/config.js";
import {
  coolingRateTriggerFpmForAircraft,
  disabledReviewMonitorKeysForAircraft,
  normalizeCoolingRateTriggerFpm,
  reviewMonitorIsEnabled,
} from "../core/preferences.js";
import {
  engineCheckDefinitions,
  metricChannelAvailability,
  monitorChannelUnavailableReason,
} from "../engine/review-model.js";
import { normalizedAircraftType } from "./aircraft-profile-model.js";

export const reviewMonitorSettingGroups = Object.freeze([
  { label: "Engine health", keys: ["sensors", "turbo", "oilSystem", "coolantMargin", "takeoffEnrichment", "cht", "egt", "cooling", "coolingRate"] },
  { label: "Event detection", keys: ["chtRhythm", "valve", "combustion", "abnormalCombustion", "divergence", "descentEgtDropout", "inductionLeak", "mag", "carbIce"] },
  { label: "Aircraft systems", keys: ["charging", "electrical", "instrumentationSuspicion"] },
  { label: "Multi-engine", keys: ["engineDelta"] },
]);

export function reviewSettingsAircraftId(value) {
  return String(value ?? "").trim();
}

export function allReviewMonitorDefinitions() {
  return new Map(engineCheckDefinitions().map(([key, label, description]) => [key, { key, label, description }]));
}

export function aircraftProfileSupportsMultiEngineReview(profile = null) {
  const normalizedType = normalizedAircraftType(profile?.aircraft?.type, profile);
  if (normalizedType === "single") return false;
  if (normalizedType === "multi" || normalizedType === "multi_centerline") return true;
  const engineCount = Number(profile?.engineLayout?.count ?? profile?.engine?.count);
  return Number.isFinite(engineCount) && engineCount > 1;
}

export function reviewMonitorGroupsForProfile(profile = null) {
  const includeMultiEngine = aircraftProfileSupportsMultiEngineReview(profile);
  return reviewMonitorSettingGroups.filter((group) => group.label !== "Multi-engine" || includeMultiEngine);
}

export function groupedReviewMonitorDefinitionsForProfile(profile = null) {
  const definitions = allReviewMonitorDefinitions();
  const monitorGroups = reviewMonitorGroupsForProfile(profile);
  const groupedKeys = new Set(reviewMonitorSettingGroups.flatMap((group) => group.keys));
  const groups = monitorGroups
    .map((group) => ({
      ...group,
      monitors: group.keys.map((key) => definitions.get(key)).filter(Boolean),
    }))
    .filter((group) => group.monitors.length);
  const remaining = [...definitions.values()].filter((definition) => !groupedKeys.has(definition.key));
  return remaining.length ? [...groups, { label: "Other", monitors: remaining }] : groups;
}

export function reviewMonitorDefinitionsForProfile(profile = null) {
  return new Map(groupedReviewMonitorDefinitionsForProfile(profile)
    .flatMap((group) => group.monitors)
    .map((monitor) => [monitor.key, monitor]));
}

export function disabledReviewMonitorKeyListForAircraft(settings, aircraft) {
  return [...disabledReviewMonitorKeysForAircraft(settings, reviewSettingsAircraftId(aircraft))];
}

export function reviewMonitorEnabledForAircraft(settings, aircraft, key) {
  return reviewMonitorIsEnabled(settings, reviewSettingsAircraftId(aircraft), key);
}

export function coolingRateTriggerSettingForAircraft(settings, aircraft) {
  return coolingRateTriggerFpmForAircraft(settings, reviewSettingsAircraftId(aircraft));
}

export function normalizeCoolingRateTriggerSetting(value) {
  return normalizeCoolingRateTriggerFpm(value);
}

export function coolingRateTriggerSettingBounds() {
  return {
    defaultFpm: chtCoolingRateTriggerFpm,
    minFpm: chtCoolingRateTriggerMinFpm,
    maxFpm: chtCoolingRateTriggerMaxFpm,
  };
}

export function reviewSettingsSignatureForAircraft(settings, aircraft) {
  const disabledSignature = disabledReviewMonitorKeyListForAircraft(settings, aircraft).sort().join("|");
  const coolingRateTrigger = coolingRateTriggerSettingForAircraft(settings, aircraft);
  return `${disabledSignature}|coolingRate:${coolingRateTrigger}`;
}

export function reviewMonitorCountTextForAircraft(settings, aircraft, profile = null) {
  const definitions = reviewMonitorDefinitionsForProfile(profile);
  const total = definitions.size;
  const disabled = disabledReviewMonitorKeyListForAircraft(settings, aircraft).filter((key) => definitions.has(key)).length;
  return disabled ? `${total - disabled} of ${total} active - ${disabled} muted` : `${total} of ${total} active`;
}

// Reads the same profile fields as profileImpliesTurbo in src/analysis/turbo.js,
// plus the aircraft editor's explicit "Turbocharged" checkbox state, so the
// dialog can pre-mark Turbo before any log exists.
function profileTurboUnavailableReason(profile) {
  if (profile?.turbo?.isTurbo === false) return "Aircraft profile is not configured as turbocharged";
  if (profile?.turbo?.isTurbo === true || profile?.isTurbo === true) return "";
  if (profile?.isTurbo === false) return "Aircraft profile is not configured as turbocharged";
  const aspiration = String(profile?.engine?.aspiration ?? profile?.turbo?.turboType ?? profile?.turboType ?? "").trim().toLowerCase();
  if (Boolean(aspiration) && !/\bturbo|super|boost/.test(aspiration)) {
    return "Aircraft profile is set to naturally aspirated";
  }
  return "";
}

// Per-aircraft monitor capability: which monitors this aircraft's instrumentation
// can actually feed. Evidence is the aircraft's analyzed flight summaries (keyed
// by monitor, e.g. summary.mag = { score, note, details }). A monitor is:
//   available    - any flight scored it, or the channel is present;
//   unavailable  - every flight with evidence hit a structural no-channel null;
//   (default)    - no evidence -> available, except Turbo, which a "naturally
//                  aspirated" profile rules out even before the first upload.
// Pass an empty summary list (no logs yet, or aircraft not loaded) and every
// monitor stays available apart from that profile-derived Turbo case.
export function aircraftMonitorCapability(summaries = [], profile = null) {
  const summaryList = (Array.isArray(summaries) ? summaries : [summaries]).filter(Boolean);
  const capability = new Map();
  for (const key of reviewMonitorDefinitionsForProfile(profile).keys()) {
    if (key === "turbo") {
      const profileReason = profileTurboUnavailableReason(profile);
      if (profileReason) {
        capability.set(key, { available: false, reason: profileReason });
        continue;
      }
    }
    let verdict = "unknown";
    for (const summary of summaryList) {
      const state = metricChannelAvailability(key, summary?.[key]);
      if (state === "available") { verdict = "available"; break; }
      if (state === "unavailable") verdict = "unavailable";
    }
    if (verdict === "available") {
      capability.set(key, { available: true, reason: "" });
    } else if (verdict === "unavailable") {
      capability.set(key, { available: false, reason: monitorChannelUnavailableReason(key) });
    } else if (key === "turbo") {
      capability.set(key, { available: true, reason: "" });
    } else {
      capability.set(key, { available: true, reason: "" });
    }
  }
  return capability;
}
